import type IProduct from "@api/models/IProduct";
import type { AppRegion } from "@core-types/region.enum";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { Group, Stack } from "@mantine/core";
import SkinAdvantages from "../../shared/components/SkinAdvantages";
import type ISkin from "../../shared/models/ISkin.model";
import { SkinProductHeader } from "./SkinProductHeader";
import { SkinProductImage } from "./SkinProductImage";
import { SkinProductRegionSelector } from "./SkinProductRegionSelector";
import { SkinProductSummary } from "./SkinProductSummary";
import { SkinProductTypeSelector } from "./SkinProductTypeSelector";

interface ISkinProductsProps {
  skin: ISkin;
  region: AppRegion;
  handleRegionChange: (region: AppRegion) => void;
  skinProducts: IProduct[];
  selectedProduct: IProduct | undefined;
  setSelectedProduct: (product: IProduct) => void;
  unavailableIds: number[];
  handleBuy: () => void;
  isOutOfStock: boolean;
}

const SkinProducts: React.FC<ISkinProductsProps> = ({
  skin,
  region,
  handleRegionChange,
  skinProducts,
  selectedProduct,
  setSelectedProduct,
  unavailableIds,
  handleBuy,
  isOutOfStock,
}) => {
  const productT = useAppTranslation();

  return (
    <>
      <Group align="start" className="w-full" justify="center">
        <SkinProductImage thumbnail={skin.thumbnail} />

        <Stack className="w-full max-w-[500px]" gap={30} align="start">
          <SkinProductHeader skinName={skin.name} />

          <SkinProductRegionSelector
            region={region}
            availableRegions={skinProducts.flatMap((p) => p.availableIn)}
            onRegionChange={handleRegionChange}
          />

          <SkinProductTypeSelector
            skinProducts={skinProducts}
            selectedProduct={selectedProduct}
            setSelectedProduct={setSelectedProduct}
            unavailableIds={unavailableIds}
          />

          <SkinProductSummary
            handleOnBuy={handleBuy}
            isOutOfStock={isOutOfStock}
            selectedProduct={selectedProduct}
            region={region}
          />
        </Stack>
      </Group>

      <SkinAdvantages
        advantages={
          (selectedProduct
            ? productT.t(`${selectedProduct.i18nKey}.details_lines`, {
                returnObjects: true,
                region: region.toUpperCase(),
              })
            : []) as string[]
        }
      />
    </>
  );
};

export default SkinProducts;
